import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { api, imageUrl, formatEUR } from "../lib/api";
import { Button } from "./ui/button";
import ProductCard from "./ProductCard";

/**
 * Phase 22.1 — Customer AI.
 *
 * Reusable horizontal product strip that fetches AI-personalized picks from
 * ``POST /api/ai/recommendations`` and renders a small carousel. Silently
 * renders nothing on any failure or empty response so it never blocks the
 * host page.
 *
 * Props:
 *   context   — "home" | "cart" | "product" (passed straight to the API).
 *   productId — optional product id when `context==="product"`.
 *   title     — the section title. Defaults to a sensible string per context.
 *   testid    — root testid.
 *   variant   — "default" | "compact" | "grid".
 *                 "default"  — horizontal scroll strip (original behaviour)
 *                 "compact"  — smaller tiles for cart drawer
 *                 "grid"     — Phase 30: responsive 2/3/4 col grid (max 8),
 *                              with a "Ver todos" CTA. Used on the home page.
 *   maxItems     — cap for the "grid" variant (defaults to 8).
 *   seeAllHref   — link target for the "Ver todos" button (grid variant only).
 *   seeAllTestId — data-testid for the "Ver todos" link (grid variant only).
 *   onNavigate — optional callback invoked when a tile is clicked (used to
 *                close the cart drawer, for instance).
 */
export default function RecoStrip({
  context,
  productId,
  title,
  testid,
  variant = "default",
  maxItems = 8,
  seeAllHref,
  seeAllTestId,
  onNavigate,
}) {
  const [items, setItems] = useState([]);
  const [reason, setReason] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .post("/ai/recommendations", { context, product_id: productId })
      .then((r) => {
        if (!alive) return;
        setItems(r.data?.products || []);
        setReason(r.data?.reason || "");
      })
      .catch(() => {
        if (!alive) return;
        setItems([]);
      })
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, productId]);

  if (!loaded || items.length === 0) return null;

  const defaultTitle = {
    home: "Para ti",
    cart: "También te puede interesar",
    product: "Los clientes también compran",
  }[context] || "Recomendaciones";

  // Phase 30 — vertical grid variant used on the home page. Renders a full
  // ProductCard for each item so it visually matches "Destacados" / "Novedades".
  if (variant === "grid") {
    const visible = items.slice(0, maxItems);
    return (
      <section
        data-testid={testid || `reco-strip-${context}`}
        className="mt-12"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-5">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#00953B]" />
            <h2
              className="text-2xl sm:text-3xl font-black tracking-tight"
              data-testid={`${testid || "reco-strip"}-title`}
            >
              {title || defaultTitle}
            </h2>
          </div>
          {reason && (
            <span className="text-xs text-neutral-500 max-w-xs truncate">
              {reason}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {visible.map((p) => (
            <div
              key={p.id}
              data-testid={`${testid || "reco-strip"}-tile-${p.slug || p.id}`}
            >
              <ProductCard product={p} />
            </div>
          ))}
        </div>
        {seeAllHref && (
          <div className="mt-6 flex justify-center">
            <Button
              asChild
              variant="outline"
              className="rounded-full border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3] px-6 h-11 font-semibold gap-2"
            >
              <Link to={seeAllHref} data-testid={seeAllTestId}>
                Ver todos <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        )}
      </section>
    );
  }

  const isCompact = variant === "compact";
  return (
    <section
      data-testid={testid || `reco-strip-${context}`}
      className={`${isCompact ? "mt-2" : "mt-12"}`}
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#00953B]" />
          <h3
            className={`font-black tracking-tight ${
              isCompact ? "text-base" : "text-2xl sm:text-3xl"
            }`}
            data-testid={`${testid || "reco-strip"}-title`}
          >
            {title || defaultTitle}
          </h3>
        </div>
        {reason && !isCompact && (
          <span className="text-xs text-neutral-500 max-w-xs truncate">
            {reason}
          </span>
        )}
      </div>
      <div
        className={`flex gap-3 overflow-x-auto pb-2 snap-x scroll-smooth [scrollbar-width:thin] ${
          isCompact ? "" : "gap-4"
        }`}
      >
        {items.map((p) => (
          <Link
            key={p.id}
            to={`/producto/${p.slug}`}
            onClick={onNavigate}
            data-testid={`${testid || "reco-strip"}-item-${p.id}`}
            className={`shrink-0 snap-start group rounded-xl border border-neutral-200 bg-white hover:border-[#00953B] hover:shadow-md transition-all overflow-hidden ${
              isCompact ? "w-[140px]" : "w-[180px] sm:w-[200px]"
            }`}
          >
            <div
              className={`bg-neutral-50 grid place-items-center overflow-hidden ${
                isCompact ? "h-24" : "h-32"
              }`}
            >
              <img
                src={imageUrl(p.image_url)}
                alt={p.name}
                loading="lazy"
                className="h-full w-full object-contain group-hover:scale-105 transition-transform"
              />
            </div>
            <div className={`p-2 ${isCompact ? "" : "p-3"}`}>
              <div className="text-[10px] uppercase tracking-wider text-neutral-400">
                {p.brand}
              </div>
              <div
                className={`font-semibold leading-tight line-clamp-2 ${
                  isCompact ? "text-xs" : "text-sm"
                }`}
              >
                {p.name}
              </div>
              <div className="mt-1 font-black text-[#00953B]">
                {formatEUR(p.price_eur)}
              </div>
              {p.reason && !isCompact && (
                <div className="mt-1 text-[11px] text-neutral-500 line-clamp-2">
                  {p.reason}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
